// Sorts saved areas into one group per country, for the Library and the area pickers. Pure: no
// React, router or Firebase imports.
import { COUNTRIES, COUNTRY_LIST, NO_COUNTRY, UNASSIGNED, areaCountry } from '../config/countries';
import type { AreaCountryKey, CountryCode } from '../config/countries';

export interface AreaCountryGroupData<T> {
  key: AreaCountryKey;
  /** Null for the "No country set" group. */
  country: CountryCode | null;
  label: string;
  flag: string;
  areas: T[];
}

/**
 * One group per country that has areas, in registry order, then "No country set" last. Areas are
 * placed by areaCountry(), so the two-letter and three-letter spellings of a country land
 * together and an unknown code lands in "No country set". Empty groups are left out and the input
 * order is kept inside each group.
 */
export function groupAreasByCountry<T extends { type: string; country_code?: string | null }>(
  areas: T[],
): AreaCountryGroupData<T>[] {
  const byKey = new Map<AreaCountryKey, T[]>();
  for (const area of areas) {
    const key: AreaCountryKey = areaCountry(area) ?? UNASSIGNED;
    const list = byKey.get(key);
    if (list) list.push(area);
    else byKey.set(key, [area]);
  }

  const groups: AreaCountryGroupData<T>[] = [];
  for (const { code } of COUNTRY_LIST) {
    const list = byKey.get(code);
    if (list) groups.push({ key: code, country: code, label: COUNTRIES[code].label, flag: COUNTRIES[code].flag, areas: list });
  }
  const unassigned = byKey.get(UNASSIGNED);
  if (unassigned) {
    groups.push({ key: UNASSIGNED, country: null, label: NO_COUNTRY.label, flag: NO_COUNTRY.flag, areas: unassigned });
  }
  return groups;
}

/** One country's areas split by area type, in the order the types first appear. */
export function groupByType<T extends { type: string }>(areas: readonly T[]): [string, T[]][] {
  const out: [string, T[]][] = [];
  for (const area of areas) {
    const existing = out.find(([type]) => type === area.type);
    if (existing) existing[1].push(area);
    else out.push([area.type, [area]]);
  }
  return out;
}
